import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, ArrowRight, Tag, User, Eye, Heart, Share2, Filter, ChevronDown, TrendingUp, Star, BookOpen } from 'lucide-react';
import Header from './header';
import Footer from './footer';
const config = window.APP_CONFIG;
const apiUrl = config.API_URL; 

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogError, setBlogError] = useState(null);
  const [isLoadingBlog, setIsLoadingBlog] = useState(true);
  const [likedPosts, setLikedPosts] = useState(new Set()); // Track liked posts
  

  // Blog categories
  const categories = [
    'All', 'Fintech', 'Technology', 'AI & ML', 'Cybersecurity', 
    'Digital Transformation', 'Industry Insights', 'Company Updates'
  ];

  // API function to update blog stats
  const postBlogStats = async (blogId, action, isLike = null) => {
    try {
      // Prepare request body based on action type
      let requestBody = {
        "BlogId": blogId,
        "Action": action
      };

      // Only include IsLike for "like" action, ignore for "view"
      if (action === "like") {
        requestBody.IsLike = isLike;
      }

      const response = await fetch(`${apiUrl}/blog/stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Blog stats updated:', data);
      return data;

    } catch (error) {
      console.error('Error updating blog stats:', error);
      throw error;
    }
  };

  // Handle like/unlike functionality
  const handleLikeToggle = async (postId) => {
    const isCurrentlyLiked = likedPosts.has(postId);
    const newLikeStatus = !isCurrentlyLiked;

    try {
      // Optimistically update the UI
      const newLikedPosts = new Set(likedPosts);
      if (newLikeStatus) {
        newLikedPosts.add(postId);
      } else {
        newLikedPosts.delete(postId);
      }
      setLikedPosts(newLikedPosts);

      // Update the blog posts state
      setBlogPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: newLikeStatus ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
            };
          }
          return post;
        })
      );

      // Call the API
      await postBlogStats(postId, "like", newLikeStatus);

    } catch (error) {
      // Revert the optimistic update on error
      const revertedLikedPosts = new Set(likedPosts);
      if (isCurrentlyLiked) {
        revertedLikedPosts.add(postId);
      } else {
        revertedLikedPosts.delete(postId);
      }
      setLikedPosts(revertedLikedPosts);

      // Revert the blog posts state
      setBlogPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: isCurrentlyLiked ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
            };
          }
          return post;
        })
      );

      console.error('Failed to update like status:', error);
    }
  };

  // Handle view tracking
  const handleViewPost = async (postId) => {
    try {
      // Update view count in state immediately
      setBlogPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              views: (post.views || 0) + 1
            };
          }
          return post;
        })
      );

      // Call the API to track the view
      await postBlogStats(postId, "view");

    } catch (error) {
      console.error('Failed to track view:', error);
      // You might want to revert the view count here if needed
    }
  };

  useEffect(() => {
    const fetchBlog = async () => {
      setIsLoadingBlog(true);
      try {
        const response = await fetch(`${apiUrl}/blog/list`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        
        // Handle different response structures
        const blogArray = data.blogs || data.articles || data.posts || data.data || data || [];
        
        const transformedBlog = Array.isArray(blogArray) ? blogArray.map((blog, index) => {
          return {
            id: blog.Id || blog.id || index + 1,
            title: blog.Title || blog.title || "Untitled Article",
            excerpt: blog.Excerpt || blog.excerpt || blog.summary || "No summary available",
            category: blog.Category || blog.category || "General",
            author: blog.Author || blog.author || "Anonymous",
            authorRole: blog.AuthorRole || blog.authorRole || blog.author_role || "Contributor",
            date: blog.Date || blog.date || blog.created_at || new Date().toISOString(),
            readTime: blog.ReadTime || blog.readTime || blog.read_time || "5 min read",
            views: blog.Views || blog.views || 0,
            likes: blog.Likes || blog.likes || 0,
            featured: blog.Featured || blog.featured || false,
            image: (blog.Images && blog.Images.length > 0) 
              ? blog.Images[0] 
              : blog.image 
              || blog.thumbnail 
              || "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop",
            videos: blog.Videos || blog.videos || [],
            docs: blog.Docs || blog.docs || [],
            tags: (blog.Tags && blog.Tags.length > 0) 
              ? blog.Tags 
              : blog.tags 
              || ["General"]
          };
        }) : [];
        
        console.log('Transformed blog posts:', transformedBlog);
        setBlogPosts(transformedBlog);
        setBlogError(null);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setBlogError(err.message);
        // Set empty array on error
        setBlogPosts([]);
      } finally {
        setIsLoadingBlog(false);
      }
    };

    fetchBlog();
  }, [apiUrl]);

  // Filter posts based on category and search
  useEffect(() => {
    let filtered = blogPosts;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => 
        post.category && post.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    if (searchQuery) {
      filtered = filtered.filter(post => 
        (post.title && post.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (post.tags && post.tags.some(tag => 
          tag && tag.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }
    
    setFilteredPosts(filtered);
  }, [selectedCategory, searchQuery, blogPosts]);

  const featuredPosts = blogPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date not available';
    }
  };

  // Loading state
  if (isLoadingBlog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (blogError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Blog Posts</h3>
          <p className="text-gray-600 mb-4">{blogError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
        <Header />
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
              AnviTech Blog
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Insights, innovations, and industry expertise from our team of technology leaders
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles, topics, technologies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl text-gray-800 text-lg focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50 shadow-xl"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{blogPosts.length}+</div>
                <div className="text-blue-200">Published Articles</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">
                  {blogPosts.reduce((sum, post) => sum + (post.views || 0), 0).toLocaleString()}+
                </div>
                <div className="text-blue-200">Total Views</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">
                  {new Set(blogPosts.map(post => post.author)).size}+
                </div>
                <div className="text-blue-200">Contributors</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Filter */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-1 overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-gray-600 text-sm">
                {filteredPosts.length} articles found
              </span>
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filter</span>
                <ChevronDown className={`w-4 h-4 transform transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Articles */}
        {selectedCategory === 'All' && featuredPosts.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center space-x-2 mb-8">
              <Star className="w-6 h-6 text-yellow-500" />
              <h2 className="text-3xl font-bold text-gray-900">Featured Stories</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredPosts.map((post, index) => (
                <article
                  key={post.id}
                  className={`group cursor-pointer ${index === 0 ? 'lg:col-span-2' : ''}`}
                >
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transform hover:scale-105 transition-all duration-500">
                    <div className={`relative ${index === 0 ? 'h-96' : 'h-64'}`}>
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                          Featured
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="text-white">
                          <div className="flex items-center space-x-4 text-sm mb-2">
                            <span className="bg-white/20 px-2 py-1 rounded-full">
                              {post.category}
                            </span>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(post.date)}</span>
                            </div>
                          </div>
                          <h3 className={`font-bold mb-2 ${index === 0 ? 'text-2xl lg:text-3xl' : 'text-xl'}`}>
                            {post.title}
                          </h3>
                          <p className="text-white/90 mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="font-medium">{post.author}</div>
                                <div className="text-sm text-white/80">{post.authorRole}</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 text-sm">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{post.readTime}</span>
                              </div>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPost(post.id);
                                }}
                                className="flex items-center space-x-1 hover:text-blue-300 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                <span>{post.views ? post.views.toLocaleString() : '0'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Regular Articles */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              <h2 className="text-3xl font-bold text-gray-900">
                {selectedCategory === 'All' ? 'Latest Articles' : `${selectedCategory} Articles`}
              </h2>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Updated daily</span>
            </div>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-600 mb-2">No articles found</h3>
              <p className="text-gray-500">
                {blogPosts.length === 0 
                  ? "No blog posts are currently available." 
                  : "Try adjusting your search or filter criteria"
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(selectedCategory === 'All' ? regularPosts : filteredPosts).map((post) => (
                <article
                  key={post.id}
                  className="group cursor-pointer bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transform hover:scale-105 transition-all duration-500"
                  onClick={() => handleViewPost(post.id)}
                >
                  <div className="relative h-48">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 text-gray-800 text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags && post.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{post.author}</div>
                          <div className="text-xs text-gray-500">{post.authorRole}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-gray-500">
                        <div className="flex items-center space-x-1 text-sm">
                          <Eye className="w-4 h-4" />
                          <span>{post.views ? post.views.toLocaleString() : '0'}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikeToggle(post.id);
                          }}
                          className={`flex items-center space-x-1 text-sm transition-colors ${
                            likedPosts.has(post.id) 
                              ? 'text-red-500 hover:text-red-600' 
                              : 'hover:text-red-500'
                          }`}
                        >
                          <Heart 
                            className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} 
                          />
                          <span>{post.likes || 0}</span>
                        </button>
                        <button className="hover:text-blue-600 transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium text-sm group">
                        <span>Read More</span>
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
   
      </div>
           <Footer/>
    </div>
  );
};

export default BlogPage;